import { Component } from '@angular/core';
import { NotificationCategory } from '../misc/enums/notification-category.enum';
import { NotificationType } from '../misc/enums/notification-type.enum';
import { DataService } from '../services/data.service';
import { Router } from '@angular/router';
import { NotificationHandlerService } from '../services/notification-handler.service';
import { GlobalConstants } from '../services/global-constants';
import { DatePipe } from '@angular/common';
import { DateService } from '../services/date.service';
import { PermissionHandlerService } from '../services/permission-handler.service';
import { TranslatorPipe } from '../services/translator.pipe';
import { MenuItem } from 'primeng/api';
import { breadcrumb } from '../admin/layout/header/header.component';

interface NotificationGroup {
  label: string;
  items: any[];
}

@Component({
  selector: 'app-notification',
  templateUrl: './notification.component.html',
  styleUrls: ['./notification.component.scss']
})
export class NotificationComponent {


  //Data Attributes
  notifications: any[] = [];
  filteredNotifications: any[] = [];

  //Filter attributes
  currentCategory: NotificationCategory = NotificationCategory.ALL;
  categoryFilters = Object.values(NotificationCategory);

  // Read-state tab, search and page shown in the main card. All three work on
  // the notifications already loaded; `goToPage` fetches more from the server
  // when a page past the loaded ones is asked for.
  readFilter: 'all' | 'unread' = 'all';
  searchTerm: string = '';
  currentPage: number = 1;
  readonly perPage: number = 10;

  // Derived from the filters above in `refreshList`, so the template does not
  // re-filter on every change detection pass.
  listNotifications: any[] = [];
  pagedGroups: NotificationGroup[] = [];
  unreadCount: number = 0;

  // Menu of the row whose ⋮ was clicked; built per row in openRowMenu
  activeRowMenu: MenuItem[] = [];

  header: string = this.translate('Notifications page');
  breadcrumbs: breadcrumb[] = [
    { name: this.translate('Notifications page'), link: '/notification' },
  ];

  private readonly categoryIcons: { [category: string]: string } = {
    [NotificationCategory.ALL]: 'pi pi-list',
    [NotificationCategory.MY]: 'pi pi-bell',
    [NotificationCategory.USER_EVENTS]: 'pi pi-users',
    [NotificationCategory.DATA_EVENTS]: 'pi pi-file-edit',
    [NotificationCategory.KLIBRARY]: 'pi pi-book',
    [NotificationCategory.ASSESSMENT]: 'pi pi-check-square',
    [NotificationCategory.PERMISSION_REQUESTS]: 'pi pi-shield',
  };

  //Comparison attributes
  userId: any = localStorage.getItem('uid');

  //Network attributes
  staticFileUrl: string = GlobalConstants.staticFilesURL;
  skelition: boolean = true;
  // placeholder rows shown while the first page loads
  readonly skeletonRows = Array(6);

  // Server pagination state. The /notification/own endpoint used to return the
  // entire history in one request, which made this page slow to open. We now
  // ask for one page at a time and append when the pager needs more.
  private readonly pageSize: number = 20;
  private nextSkip: number = 0;
  hasMore: boolean = true;
  loadingMore: boolean = false;
  // Cap auto-loads triggered when an entire raw page gets dropped by client
  // permission filters, to avoid pathological loops on fully-restricted users.
  private readonly maxAutoLoadChain: number = 5;
  // Guards the auto "mark all as seen" call so we only fire it once per
  // page load (and never twice in flight).
  private markingAllSeen: boolean = false;


  constructor(
    private dataService: DataService,
    private router: Router,
    private notificationHandler: NotificationHandlerService,
    private dateService: DateService,
    private permissionService: PermissionHandlerService
  ) { }

  private datePipe: DatePipe = new DatePipe('en-US', 'Asia/Riyadh');

  ngOnInit() {
    this.filterCategories();
    this.getNotificaitons();
    this.notificationHandler.getNotifications().subscribe(notificaiton => {
      this.notifications.unshift(notificaiton);
      if (this.matchesCurrentFilter(notificaiton)) {
        this.filteredNotifications.unshift(notificaiton);
      }
      this.refreshList();
    });
  }

  filterCategories() {
    this.categoryFilters = this.categoryFilters.filter(e => {
      if (e == NotificationCategory.DATA_EVENTS) {
        return this.permissionService.hasPermission('DATA', 'r') ||
          this.permissionService.hasPermission('CONTENT', 'r') ||
          this.permissionService.hasPermission('CONTENT_APPROVE', 'a') ||
          this.permissionService.hasPermission('COMMENTS', 'r');
      } else if (e == NotificationCategory.USER_EVENTS) {
        return this.permissionService.hasPermission('USER_MANAGEMENT', 'r');
      } else if (e == NotificationCategory.KLIBRARY) {
        return this.permissionService.hasPermission('KNOWLEDGE_LIBRARY', 'r');
      } else if (e == NotificationCategory.MY) {
        return true;
      } else if (e == NotificationCategory.PERMISSION_REQUESTS) {
        return this.permissionService.hasPermission('PERMISSION_REQUESTS', 'r');
      } 
      else if (e == NotificationCategory.ALL) {
        return (this.permissionService.hasPermission('DATA', 'r') ||
          this.permissionService.hasPermission('CONTENT', 'r') ||
          this.permissionService.hasPermission('CONTENT_APPROVE', 'a') ||
          this.permissionService.hasPermission('COMMENTS', 'r')) &&
          this.permissionService.hasPermission('USER_MANAGEMENT', 'r') &&
          this.permissionService.hasPermission('KNOWLEDGE_LIBRARY', 'r');
      }
      return false;
    });

    // If the default `currentCategory` (ALL) was filtered out for this user
    // we need to fall back to the first available filter, otherwise no
    // sidebar button will visually match `currentCategory` and the user
    // sees an indistinguishable list of unselected buttons.
    if (!this.categoryFilters.includes(this.currentCategory) && this.categoryFilters.length > 0) {
      this.currentCategory = this.categoryFilters[0];
    }
  }

  changeCurrentFilter(filter: NotificationCategory) {
    this.currentCategory = filter;
    this.filteredNotifications = this.notifications.filter((n: any) => this.matchesCurrentFilter(n));
    this.currentPage = 1;
    this.refreshList();
  }

  changeReadFilter(filter: 'all' | 'unread') {
    this.readFilter = filter;
    this.currentPage = 1;
    this.refreshList();
  }

  onSearchChange() {
    this.currentPage = 1;
    this.refreshList();
  }

  /**
   * Applies the read tab and search on top of the category filter, then
   * slices out the current page and groups it by day.
   */
  refreshList() {
    const term = this.searchTerm.trim().toLowerCase();
    this.unreadCount = this.filteredNotifications.filter((n: any) => !this.isSeen(n)).length;
    this.listNotifications = this.filteredNotifications.filter((n: any) => {
      if (this.readFilter == 'unread' && this.isSeen(n)) {
        return false;
      }
      if (!term) {
        return true;
      }
      return [n?.title, n?.message, n?.name]
        .some((text: any) => typeof text == 'string' && text.toLowerCase().includes(term));
    });
    if (this.currentPage > this.totalPages) {
      this.currentPage = this.totalPages;
    }
    const start = (this.currentPage - 1) * this.perPage;
    this.pagedGroups = this.groupByDay(this.listNotifications.slice(start, start + this.perPage));
  }

  private groupByDay(items: any[]): NotificationGroup[] {
    const groups: NotificationGroup[] = [];
    for (const n of items) {
      const label = this.dayLabel(n.createdAt);
      const last = groups[groups.length - 1];
      if (last && last.label == label) {
        last.items.push(n);
      } else {
        groups.push({ label, items: [n] });
      }
    }
    return groups;
  }

  private daysAgo(date: any): number {
    const day = new Date(date);
    day.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.round((today.getTime() - day.getTime()) / (24 * 60 * 60 * 1000));
  }

  private dayLabel(date: any): string {
    const days = this.daysAgo(date);
    if (days <= 0) return this.translate('Today');
    if (days == 1) return this.translate('Yesterday');
    return this.translate('Older');
  }

  /**
   * Relative time for today ("منذ 10 دقائق"), "أمس، 14:20" for yesterday and
   * the full date before that.
   */
  getTimeLabel(date: any): string {
    const days = this.daysAgo(date);
    if (days <= 0) {
      return this.getDateDifference(date) || '';
    }
    const time = this.datePipe.transform(date, 'HH:mm') || '';
    if (days == 1) {
      return `${this.translate('Yesterday')}، ${time}`;
    }
    return `${this.datePipe.transform(date, 'dd/MM/yyyy') || ''}، ${time}`;
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.listNotifications.length / this.perPage));
  }

  get pageStart(): number {
    return this.listNotifications.length == 0 ? 0 : (this.currentPage - 1) * this.perPage + 1;
  }

  get pageEnd(): number {
    return Math.min(this.currentPage * this.perPage, this.listNotifications.length);
  }

  get pageNumbers(): number[] {
    const total = this.totalPages;
    const current = this.currentPage;
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const pages = [1];
    if (current > 3) pages.push(-1);
    for (let p = Math.max(2, current - 1); p <= Math.min(total - 1, current + 1); p++) pages.push(p);
    if (current < total - 2) pages.push(-1);
    pages.push(total);
    return pages;
  }

  /**
   * Moves to `page`, first fetching server pages until it is filled (or the
   * server runs out), since the total count is not known up front.
   */
  async goToPage(page: number) {
    if (page < 1) return;
    let fetches = 0;
    while (page * this.perPage > this.listNotifications.length && this.hasMore && fetches < this.maxAutoLoadChain) {
      await this.loadPage();
      fetches++;
    }
    if (page > this.totalPages) return;
    this.currentPage = page;
    this.refreshList();
  }

  private matchesCurrentFilter(n: any): boolean {
    if (this.currentCategory == NotificationCategory.ALL) {
      return true;
    } else if (this.currentCategory == NotificationCategory.MY) {
      return n?.sender?._id == this.userId;
    }
    return n?.category == this.currentCategory;
  }

  async getNotificaitons() {
    this.skelition = true;
    this.nextSkip = 0;
    this.hasMore = true;
    this.notifications = [];
    this.filteredNotifications = [];
    try {
      await this.loadPage();
    } finally {
      this.skelition = false;
    }
  }

  /**
   * Fetches a single page from `notification/own`. When a fetched page is
   * fully dropped by client-side permission filters but more pages exist,
   * automatically chains to the next page (up to `maxAutoLoadChain`) so the
   * user does not see an empty state when there are still notifications to
   * surface.
   */
  private async loadPage(autoLoadDepth: number = 0): Promise<void> {
    if (this.loadingMore || !this.hasMore) {
      return;
    }
    this.loadingMore = true;
    try {
      const ous_allowed = localStorage.getItem('all_ou');
      const res = await this.dataService.postData('notification/own', {
        ous: ous_allowed ? JSON.parse(ous_allowed) : [],
        skip: this.nextSkip,
        limit: this.pageSize,
      });
      if (res?.error) {
        return;
      }
      const rawPage: any[] = Array.isArray(res?.data) ? res.data : [];
      this.nextSkip += rawPage.length;
      // If the backend returned fewer than a full page, there is nothing more.
      this.hasMore = rawPage.length === this.pageSize;

      const visiblePage = rawPage
        .filter((e: any) =>
          this.notificationHandler.applyPermissions(e) &&
          this.notificationHandler.applyDatePermission(e)
        )
        .map((n: any) => {
          this.notificationHandler.setNotificationTypeMessage(n);
          return n;
        });

      if (visiblePage.length > 0) {
        this.notifications = this.notifications
          .concat(visiblePage)
          .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        this.filteredNotifications = this.notifications.filter((n: any) => this.matchesCurrentFilter(n));
        this.refreshList();
      }

      // Auto-chain when the entire page got filtered out client-side but the
      // server still has more, so the user is not left looking at an empty
      // list when more pages would surface visible items.
      if (visiblePage.length === 0 && this.hasMore && autoLoadDepth < this.maxAutoLoadChain) {
        this.loadingMore = false;
        await this.loadPage(autoLoadDepth + 1);
        return;
      }
    } catch (e) {
      console.error(e);
    } finally {
      this.loadingMore = false;
    }
  }

  /**
   * Used by *ngFor trackBy so refreshing the list does not re-render the
   * already-visible rows.
   */
  trackByNotificationId(_index: number, n: any): string {
    return n?._id || _index;
  }

  get hasUnseenNotifications(): boolean {
    const userId = localStorage.getItem('uid');
    if (!userId) return false;
    return this.notifications.some((n: any) =>
      Array.isArray(n?.seenBy) && !n.seenBy.includes(userId)
    );
  }

  isSeen(notification: any): boolean {
    if (!this.userId) {
      return false;
    }
    if (!Array.isArray(notification?.seenBy)) {
      return false;
    }
    return notification.seenBy.includes(this.userId);
  }

  /**
   * Row click: mark the notification as seen and open its target.
   */
  openNotification(notification: any) {
    if (!notification) {
      return;
    }
    this.markRead(notification);
    this.notificationHandler.navigateToRoute(notification);
  }

  /**
   * Mark a single notification as seen. Local state is updated optimistically
   * so the row switches to "read" before the network round-trip completes; if
   * the API call fails we revert. Defensively initialises a missing `seenBy`
   * array so legacy documents still mark correctly.
   */
  async markRead(notification: any) {
    const userId = localStorage.getItem('uid');
    if (!notification || !userId) {
      return;
    }
    if (!Array.isArray(notification.seenBy)) {
      notification.seenBy = [];
    }
    if (notification.seenBy.includes(userId)) {
      return;
    }

    const previousSeenBy = [...notification.seenBy];
    notification.seenBy.push(userId);
    this.refreshList();
    try {
      const res = await this.dataService.postData('notification/' + notification._id, {}, 'PUT', false);
      if (res?.error) {
        notification.seenBy = previousSeenBy;
        this.refreshList();
      }
    } catch (e) {
      console.warn('Failed to mark notification as seen', e);
      notification.seenBy = previousSeenBy;
      this.refreshList();
    }
  }

  openRowMenu(menu: any, event: Event, notification: any) {
    event.stopPropagation();
    const items: MenuItem[] = [
      {
        label: this.translate('Open'),
        icon: 'pi pi-external-link',
        command: () => this.openNotification(notification),
      },
    ];
    if (!this.isSeen(notification)) {
      items.push({
        label: this.translate('Mark as read'),
        icon: 'pi pi-check',
        command: () => this.markRead(notification),
      });
    }
    this.activeRowMenu = items;
    menu.toggle(event);
  }

  /**
   * Bulk-mark every notification as seen for the current user, from the
   * "mark all as read" button.
   */
  async markAllSeen() {
    if (this.markingAllSeen) {
      return;
    }
    const userId = localStorage.getItem('uid');
    if (!userId) {
      return;
    }
    const unseen = this.notifications.filter((n: any) =>
      Array.isArray(n?.seenBy) && !n.seenBy.includes(userId)
    );
    if (unseen.length === 0) {
      return;
    }
    this.markingAllSeen = true;
    try {
      for (const n of unseen) {
        n.seenBy.push(userId);
      }
      this.refreshList();
      const res = await this.dataService.postData('notification/seen-all', {}, 'PUT', false);
      if (res?.error) {
        for (const n of unseen) {
          n.seenBy = (n.seenBy || []).filter((id: string) => id !== userId);
        }
      }
    } catch (e) {
      console.warn('Failed to mark all notifications as seen', e);
      for (const n of unseen) {
        n.seenBy = (n.seenBy || []).filter((id: string) => id !== userId);
      }
    } finally {
      this.markingAllSeen = false;
      this.refreshList();
    }
  }


  getDateDifference(date: any) {
    return this.dateService.getDataDiff(date, new Date())
  }

  translate(key: string) {
    let translator: TranslatorPipe = new TranslatorPipe();
    return translator.transform(key);
  }

  getCategoryArabics(category: string) {
    if (category == NotificationCategory.KLIBRARY) {
      return this.translate(`${category} Events`)
    }
    return this.translate(category);
  }

  getCategoryIcon(category: string): string {
    return this.categoryIcons[category] || 'pi pi-bell';
  }

  /**
   * Icon in the row's tile: a check for approvals, otherwise the icon of the
   * notification's category.
   */
  getNotificationIcon(notification: any): string {
    if (notification?.type == NotificationType.PERMISSION_REQUEST_APPROVED) {
      return 'pi pi-check-circle';
    }
    if (notification?.type == NotificationType.DATA_UPDATE) {
      return 'pi pi-file';
    }
    return this.getCategoryIcon(notification?.category);
  }

  /**
   * Label of the row's action button; null for notifications that only
   * inform and have nothing to act on.
   */
  getActionLabel(notification: any): string | null {
    switch (notification?.type) {
      case NotificationType.CONTENT_RECEIVED_FOR_APPROVAL:
      case NotificationType.PERMISSION_REQUEST_CREATED:
      case NotificationType.COURSE_ADD_REQUEST:
        return this.translate('Review Request');
      case NotificationType.USER_REGISTER:
        return this.translate('View Details');
      case NotificationType.KLIBRARY_NOTIFICATION:
        return this.translate('View Content');
      default:
        return null;
    }
  }


}
